import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

// -------------------------------------------------------
// StatCard — card KPI/riepilogo standard (DESIGN.md §1/§6/§7).
// Superficie bianca + hairline, icona in chip tinta tenue, numero grande
// tabulare, label muted. NIENTE sfondi sfumati/aloni decorativi.
// -------------------------------------------------------

type Tone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger'

const toneMap: Record<Tone, string> = {
  neutral: 'bg-slate-100 text-slate-500',
  primary: 'bg-primary-50 text-primary-600',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  danger: 'bg-danger-50 text-danger-600',
}

export interface StatCardProps {
  label: string
  value: React.ReactNode
  icon?: LucideIcon
  tone?: Tone
  hint?: React.ReactNode
  onClick?: () => void
  active?: boolean
  className?: string
}

export function StatCard({ label, value, icon: Icon, tone = 'neutral', hint, onClick, active, className }: StatCardProps) {
  const interactive = Boolean(onClick)
  const content = (
    <>
      <div className="flex items-center gap-2">
        {Icon && (
          <span className={cn('flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0', toneMap[tone])}>
            <Icon className="w-4 h-4" />
          </span>
        )}
        <span className="text-xs font-medium text-slate-500 truncate">{label}</span>
      </div>
      <div className="text-2xl font-semibold text-slate-900 tabular-nums leading-none">{value}</div>
      {hint && <div className="text-xs text-slate-400">{hint}</div>}
    </>
  )

  const classes = cn(
    'flex flex-col gap-2.5 rounded-2xl border bg-white p-5 text-left shadow-card',
    active ? 'border-primary-300 ring-1 ring-primary-500/20' : 'border-slate-100',
    interactive && 'hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] cursor-pointer w-full',
    className
  )

  if (interactive) {
    return (
      <button type="button" onClick={onClick} className={classes}>
        {content}
      </button>
    )
  }
  return <div className={classes}>{content}</div>
}
